/**
 * toResearchResponse — the ONE adapter between raw research output and the UI.
 *
 * Used by both paths that produce a research answer:
 *   1. the `research.completed` WebPubSub event  (`data` object)
 *   2. the blob / history load                   (`researchGetMessageContent`)
 *
 * Both shapes are accepted; the adapter normalises them into a single
 * `ResearchResponse` envelope without dropping any citation provenance.
 */

import {
  BulletsPayload,
  ChronologyPayload,
  CitationLocator,
  CitationOffsetSource,
  CitationSection,
  DraftPayload,
  Envelope,
  IntentSource,
  MemoPayload,
  ProsePayload,
  ResearchCitation,
  ResearchIntent,
  ResearchResponse,
  SourceListPayload,
  LocatorStrategy,
  isResearchIntent,
} from './types';
import { cuesFromBlob, interimFromBlob, interimTexts } from './interim';

/* ------------------------------------------------------------------ */
/*  Small coercion helpers                                             */
/* ------------------------------------------------------------------ */

const isObject = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

const str = (v: unknown, fallback = ''): string => {
  if (typeof v === 'string') return v;
  if (v === null || v === undefined) return fallback;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return fallback;
};

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const strArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.trim() !== '') : [];

const OFFSET_SOURCES: CitationOffsetSource[] = ['', 'chunker', 'snippet_match', 'unavailable'];

const offsetSource = (v: unknown): CitationOffsetSource =>
  (OFFSET_SOURCES as string[]).includes(str(v)) ? (str(v) as CitationOffsetSource) : '';

/** Text that may arrive as a nested object (older blobs). Never returns non-strings. */
const asText = (v: unknown): string => {
  if (typeof v === 'string') return v;
  if (v === null || v === undefined) return '';
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
};

/* ------------------------------------------------------------------ */
/*  Citations                                                          */
/* ------------------------------------------------------------------ */

/**
 * Map one raw citation, preserving every provenance field.
 *
 * Accepts both the new engine shape and the older raw-blob shape
 * (`source_key`, `content`, `external_url`).
 */
export function toResearchCitation(raw: any, index: number): ResearchCitation {
  const c = isObject(raw) ? raw : {};
  return {
    id: str(c.id),
    number: numOrNull(c.number) ?? index + 1,
    title: str(c.title),
    snippet: str(c.snippet) || (c.content ? asText(c.content).slice(0, 500) : ''),
    section: str(c.section) || str(c.source_key),
    law: str(c.law),
    paragraph: str(c.paragraph),
    url: str(c.url) || str(c.external_url),
    ...(c.favicon ? { favicon: str(c.favicon) } : {}),

    provider: str(c.provider),
    source_type: str(c.source_type),
    relevance_tier: str(c.relevance_tier),
    score: numOrNull(c.score),
    start_char: numOrNull(c.start_char),
    end_char: numOrNull(c.end_char),
    offset_source: offsetSource(c.offset_source),
    file_name: str(c.file_name),
    document_uuid: str(c.document_uuid),
    blob_path: str(c.blob_path) || str(c.path) || str(c.source_path),
    index_name: str(c.index_name),

    marker: str(c.marker) || `[${numOrNull(c.number) ?? index + 1}]`,
    document_type: str(c.document_type),
    authority: str(c.authority),
    jurisdiction: str(c.jurisdiction),
    date: str(c.date) || str(c.published_at),
    document_number: str(c.document_number),
    pinpoint: str(c.pinpoint),

    // The verbatim passage. Never defaulted from a model field: a source with
    // no quote renders without one.
    quote: str(c.quote),
    quote_start: numOrNull(c.quote_start),
    quote_end: numOrNull(c.quote_end),
    quote_exact: c.quote_exact === true,
    // `asserts` is what `supports` always was — the ANSWER's own sentence. A
    // blob written before the rename carries only `supports`, so it maps onto
    // `asserts` here and is tagged as generated, which is exactly what it was.
    asserts: str(c.asserts) || str(c.supports),
    asserts_kind: str(c.asserts_kind) || 'model_generated',
    supports: str(c.supports) || str(c.asserts),
    justification: str(c.justification),
    container: str(c.container),
    locator: toCitationLocator(c.locator),
  };
}

/**
 * The locator, normalised.
 *
 * `strategy` is trusted when the engine names one — it says which form is
 * STRONGEST — but is derived from the fields present when it is missing, so
 * an older or partial locator still resolves to something usable.
 */
export function toCitationLocator(raw: unknown): CitationLocator | null {
  if (!isObject(raw)) return null;

  const textFragment = str(raw.text_fragment);
  const quote = str(raw.quote);
  const heading = str(raw.heading);
  const precision = str(raw.precision);
  const startChar = numOrNull(raw.start_char);
  const endChar = numOrNull(raw.end_char);
  const chunk = numOrNull(raw.chunk);

  const named = str(raw.strategy) as LocatorStrategy;
  const known: LocatorStrategy[] =
    ['text_fragment', 'quote', 'char_range', 'heading', 'chunk', 'none'];

  const derived: LocatorStrategy =
    textFragment ? 'text_fragment'
      : quote ? 'quote'
      : startChar !== null && precision !== 'unavailable' ? 'char_range'
      : heading ? 'heading'
      : chunk !== null ? 'chunk'
      : 'none';

  return {
    strategy: known.includes(named) ? named : derived,
    text_fragment: textFragment,
    quote,
    start_char: startChar,
    end_char: endChar,
    precision,
    heading,
    chunk,
    sections: toCitationSections(raw.sections),
  };
}

/** The matching passages, in the order the engine ranked them. */
export function toCitationSections(raw: unknown): CitationSection[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(isObject)
    .map((section) => ({
      heading: str(section.heading),
      start_char: numOrNull(section.start_char),
      end_char: numOrNull(section.end_char),
      precision: str(section.precision),
      chunk_index: numOrNull(section.chunk_index),
      excerpt: str(section.excerpt),
    }))
    // A section with nothing to aim at cannot be jumped to.
    .filter((section) =>
      !!section.heading || !!section.excerpt ||
      section.start_char !== null || section.chunk_index !== null
    );
}

export function toResearchCitations(raw: unknown): ResearchCitation[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(toResearchCitation);
}

/* ------------------------------------------------------------------ */
/*  Intent inference                                                   */
/* ------------------------------------------------------------------ */

/** True when an object looks like the memo/synthesis schema. */
export function looksLikeMemo(v: unknown): boolean {
  return isObject(v) && !!(v.executive_summary || v.detailed_analysis || v.synthesis_date);
}

/** Parse a JSON string that may hold a memo payload. Returns null otherwise. */
export function tryParseMemoJson(text: unknown): MemoPayload | null {
  if (typeof text !== 'string' || !text.trimStart().startsWith('{')) return null;
  try {
    const parsed = JSON.parse(text);
    return looksLikeMemo(parsed) ? (parsed as MemoPayload) : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Payload validation                                                 */
/* ------------------------------------------------------------------ */

const nonEmptyArray = (v: unknown): boolean => Array.isArray(v) && v.length > 0;
const nonEmptyString = (v: unknown): boolean => typeof v === 'string' && v.trim() !== '';

/**
 * Does this payload carry enough for its typed renderer to say anything?
 *
 * An empty or wrong-shaped payload is NOT usable — the dispatcher must fall
 * back to the server markdown rather than render an empty typed view.
 */
export function isPayloadUsable(intent: ResearchIntent, payload: unknown): boolean {
  if (!isObject(payload)) return false;

  switch (intent) {
    case 'memo':
    case 'deep_research':
      return looksLikeMemo(payload);
    case 'qa':
    case 'overview':
      return (
        nonEmptyString(payload.answer) ||
        nonEmptyString(payload.prose) ||
        nonEmptyArray(payload.key_points) ||
        nonEmptyArray(payload.sections)
      );
    case 'chronology':
      return nonEmptyArray(payload.events);
    case 'bullets':
      return nonEmptyArray(payload.bullets) || nonEmptyArray(payload.groups);
    case 'find_document':
      return nonEmptyArray(payload.documents);
    case 'draft':
      // `body_markdown` IS the deliverable — without it there is no document,
      // and `render_draft` returns "" for the same reason.
      return nonEmptyString(payload.body_markdown);
    default:
      return false;
  }
}

/* ------------------------------------------------------------------ */
/*  Adapter                                                            */
/* ------------------------------------------------------------------ */

export interface ToResearchResponseOptions {
  messageUuid?: string;
  /** The intent the user pinned on the composer, when they pinned one. */
  explicitIntent?: ResearchIntent;
  correlationId?: string;
  /** Retrieval block for the citation side panel, when fetched separately. */
  retrieval?: Record<string, any>;
  /** Fallback markdown (e.g. the SQL `summary`) when the source carries none. */
  fallbackMarkdown?: string;
}

/**
 * Build a `ResearchResponse` envelope from a completion-event `data` object,
 * a blob result, or anything close enough to either.
 *
 * Never throws — an unrecognised or malformed source yields a `qa` envelope
 * whose `markdown` holds whatever text could be recovered and whose
 * `meta.payloadValid` is false, so the dispatcher falls back to markdown.
 */
export function toResearchResponse(
  source: unknown,
  options: ToResearchResponseOptions = {}
): ResearchResponse {
  const src: Record<string, any> = isObject(source) ? source : {};

  // The blob nests the typed result under `synthesis`; the event does not.
  const synthesis: Record<string, any> = isObject(src.synthesis) ? src.synthesis : {};

  /* ── markdown ───────────────────────────────────────────────── */
  // Order mirrors the two legacy paths this replaces: server-rendered
  // markdown wins, then the blob's `synthesis.content`, then the
  // backward-compatible `final_response` / `text`.
  const markdown =
    asText(
      src.synthesis_markdown ||
        synthesis.synthesis_markdown ||
        src.markdown ||
        synthesis.markdown ||
        synthesis.content ||
        src.final_response ||
        synthesis.final_response ||
        src.text ||
        src.content ||
        ''
    ) || asText(options.fallbackMarkdown);

  /* ── citations ──────────────────────────────────────────────── */
  const citations = toResearchCitations(
    (Array.isArray(synthesis.citations) && synthesis.citations.length && synthesis.citations) ||
      (Array.isArray(src.citations) && src.citations) ||
      []
  );

  /* ── conversational lines ───────────────────────────────────
   * Model-written, in the session's output language, and empty when the
   * model declined. Both spellings carry the same sanitized value; there is
   * no FE fallback by design.
   */
  const leadIn = asText(synthesis.leadIn || src.leadIn || synthesis.lead_in || src.lead_in || '');
  const closing = asText(
    synthesis.closing ||
      src.closing ||
      synthesis.follow_up_message ||
      src.follow_up_message ||
      ''
  );
  const documentTitle = asText(
    synthesis.documentTitle ||
      src.documentTitle ||
      synthesis.document_title ||
      src.document_title ||
      ''
  );
  // Either the event payload or the blob may carry them.
  const interim = interimTexts([
    ...interimFromBlob(synthesis),
    ...interimFromBlob(src),
  ].filter((m, i, all) => all.findIndex((o) => o.text === m.text) === i));
  // Either half of the blob may carry the cue list; the first one that has
  // it wins, and neither having it is the normal fast-path case.
  const cues = cuesFromBlob(synthesis).length ? cuesFromBlob(synthesis) : cuesFromBlob(src);
  const followUp = strArray(
    synthesis.followUp ||
      src.followUp ||
      synthesis.follow_up ||
      src.follow_up ||
      synthesis.follow_up_questions ||
      src.follow_up_questions ||
      []
  );

  /* ── intent + payload ───────────────────────────────────────── */
  const rawIntent = src.intent ?? synthesis.intent;
  const rawIntentValue = rawIntent;
  let intent: ResearchIntent;
  let intentSource: IntentSource;

  let payload: unknown =
    (isObject(src.payload) && src.payload) ||
    (isObject(synthesis.payload) && synthesis.payload) ||
    undefined;

  /**
   * The blob nests the typed result under `synthesis` WITHOUT a `payload`
   * wrapper — a stored draft is `{ synthesis: { body_markdown, document_type,
   * … } }`. Only the memo shape used to be lifted from there, so every other
   * stored artifact fell through to raw markdown on history hydration and
   * rendered as unformatted text instead of a document.
   *
   * Lifting is validated against the RESOLVED intent, so this can only ever
   * promote an object the typed renderer for that intent can actually use.
   */
  const declaredIntent = isResearchIntent(rawIntentValue)
    ? rawIntentValue
    : options.explicitIntent && isResearchIntent(options.explicitIntent)
    ? options.explicitIntent
    : null;

  if (payload === undefined) {
    if (declaredIntent && isPayloadUsable(declaredIntent, synthesis)) payload = synthesis;
    else if (declaredIntent && isPayloadUsable(declaredIntent, src)) payload = src;
    else if (looksLikeMemo(synthesis)) payload = synthesis;
    else if (looksLikeMemo(src)) payload = src;
    else {
      const parsed = tryParseMemoJson(src.final_response ?? src.text ?? src.content);
      if (parsed) payload = parsed;
    }
  }

  if (isResearchIntent(rawIntent)) {
    intent = rawIntent;
    // Only 'explicit' when the engine actually honoured the pinned intent.
    intentSource = options.explicitIntent === rawIntent ? 'explicit' : 'server';
  } else if (options.explicitIntent) {
    intent = options.explicitIntent;
    intentSource = 'explicit';
  } else if (looksLikeMemo(payload)) {
    intent = 'memo';
    intentSource = 'inferred';
  } else {
    intent = 'qa';
    intentSource = 'fallback';
  }

  const payloadValid = isPayloadUsable(intent, payload);

  const meta = {
    intentSource,
    payloadValid,
    ...(options.messageUuid ? { messageUuid: options.messageUuid } : {}),
    ...(src.payload_schema ? { payloadSchema: str(src.payload_schema) } : {}),
    ...(options.correlationId ? { correlationId: options.correlationId } : {}),
    ...(src.chat_name ? { chatName: str(src.chat_name) } : {}),
    ...(numOrNull(src.processing_time_ms) !== null
      ? { processingTimeMs: numOrNull(src.processing_time_ms) as number }
      : {}),
    ...(src.model_used ? { modelUsed: str(src.model_used) } : {}),
    ...(isObject(options.retrieval)
      ? { retrieval: options.retrieval }
      : isObject(src.retrieval)
      ? { retrieval: src.retrieval }
      : {}),
    // The engine's own artifact verdict, when it sent one.
    ...(typeof (src.is_artifact ?? synthesis.is_artifact) === 'boolean'
      ? { isArtifact: !!(src.is_artifact ?? synthesis.is_artifact) }
      : {}),
    // R5/R6 annotations ride alongside `payload`, not inside it.
    ...(isObject(src.verification) ? { verification: src.verification } : {}),
    ...(isObject(src.draft_review) ? { draftReview: src.draft_review } : {}),
    raw: source,
  };

  const base = {
    markdown, citations, leadIn, closing, documentTitle, interim, cues, followUp, meta,
  };
  const safePayload = isObject(payload) ? payload : {};

  switch (intent) {
    case 'memo':
      return { ...base, intent: 'memo', payload: safePayload as MemoPayload };
    case 'deep_research':
      return { ...base, intent: 'deep_research', payload: safePayload as MemoPayload };
    case 'overview':
      return { ...base, intent: 'overview', payload: safePayload as ProsePayload };
    case 'chronology':
      return { ...base, intent: 'chronology', payload: safePayload as ChronologyPayload };
    case 'bullets':
      return { ...base, intent: 'bullets', payload: safePayload as BulletsPayload };
    case 'find_document':
      return { ...base, intent: 'find_document', payload: safePayload as SourceListPayload };
    case 'draft':
      return { ...base, intent: 'draft', payload: safePayload as DraftPayload };
    case 'qa':
    default:
      return { ...base, intent: 'qa', payload: safePayload as ProsePayload };
  }
}

/** Convenience: the memo payload of an envelope, or null when not a memo. */
export function memoPayloadOf(
  response: ResearchResponse
): MemoPayload | null {
  if (response.intent !== 'memo' && response.intent !== 'deep_research') return null;
  if (!response.meta.payloadValid) return null;
  return response.payload as MemoPayload;
}

export type { Envelope };
